import { NavLink } from 'react-router-dom';
import type { ReactNode } from 'react';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
}

// Home is a placeholder until phase 5's HomeView ships — point at the
// People grid so v2 users land somewhere meaningful.
const TOP: NavItem[] = [
  { to: '/people', label: 'Home', icon: <IconHome /> }
];

const MAIN: NavItem[] = [
  { to: '/companies', label: 'Companies', icon: <IconCompany /> },
  { to: '/people', label: 'People', icon: <IconPerson /> },
  { to: '/dial', label: 'Lists', icon: <IconList /> },
  { to: '/audit', label: 'Activity', icon: <IconActivity /> },
  { to: '/colleagues', label: 'Colleagues', icon: <IconColleagues /> }
];

const BOTTOM: NavItem[] = [
  { to: '/trash', label: 'Trash', icon: <IconTrash /> },
  { to: '/settings', label: 'Settings', icon: <IconSettings /> }
];

export function Sidebar({ workspaceLabel, footer }: { workspaceLabel: string; footer?: ReactNode }) {
  return (
    <aside
      className="surface border-r border-divider flex flex-col h-screen sticky top-0 w-56 shrink-0"
      aria-label="Primary"
    >
      <div className="px-3 py-3 border-b border-divider">
        <p className="text-[10px] uppercase tracking-widest text-muted">Personical</p>
        <p className="text-sm font-semibold text-strong truncate">{workspaceLabel}</p>
      </div>

      <nav className="flex-1 px-2 py-3 space-y-3 text-sm overflow-hidden">
        <Group items={TOP} />
        <Group items={MAIN} />
      </nav>

      <div className="border-t border-divider px-2 py-3 space-y-1 text-sm">
        <Group items={BOTTOM} />
        {footer}
      </div>
    </aside>
  );
}

function Group({ items }: { items: NavItem[] }) {
  return (
    <ul className="space-y-0.5">
      {items.map((item) => (
        <li key={item.to + item.label}>
          <NavLink
            to={item.to}
            className={({ isActive }) =>
              `flex items-center gap-2 rounded-md px-2 py-1.5 transition ${
                isActive ? 'surface-muted text-strong' : 'text-mid hover:surface-hover'
              }`
            }
            end={item.to === '/contacts' && !item.to.includes('?')}
          >
            <span className="shrink-0 text-faint" aria-hidden>
              {item.icon}
            </span>
            <span className="truncate">{item.label}</span>
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

const ICON_PROPS = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const
};

function IconHome() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M3 11l9-8 9 8" />
      <path d="M5 10v10h14V10" />
    </svg>
  );
}
function IconCompany() {
  return (
    <svg {...ICON_PROPS}>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M9 8h.01M9 12h.01M9 16h.01M15 8h.01M15 12h.01M15 16h.01" />
    </svg>
  );
}
function IconPerson() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
    </svg>
  );
}
function IconList() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M8 6h13M8 12h13M8 18h13" />
      <circle cx="4" cy="6" r="1" />
      <circle cx="4" cy="12" r="1" />
      <circle cx="4" cy="18" r="1" />
    </svg>
  );
}
function IconActivity() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M3 12h4l3-8 4 16 3-8h4" />
    </svg>
  );
}
function IconColleagues() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="9" cy="8" r="3" />
      <circle cx="17" cy="10" r="2" />
      <path d="M3 20c0-3 3-5 6-5s6 2 6 5" />
      <path d="M15 20c0-2 2-3.5 4-3.5s2 .5 2 .5" />
    </svg>
  );
}
function IconTrash() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />
    </svg>
  );
}
function IconSettings() {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3 1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8 1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  );
}
